import { CloseButton, Combobox, Input, InputBase, Loader, useCombobox } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { groupsQueryOptions } from "shared/graphql/groups";

const none_literal = "NONE" as const;

export const GroupSearchCombobox = ({
    required,
    selectedGroup,
    setSelectedGroup,
    nullable,
    fullWidth,
    disabled,
}: { required?: boolean; fullWidth?: boolean; disabled?: boolean } & (
    | {
          nullable: false;
          setSelectedGroup: (opts: { id: string; name: string }) => unknown;
          selectedGroup: { id: string; name: string };
      }
    | {
          nullable: true;
          setSelectedGroup: (opts: { id: string; name: string } | null | undefined) => unknown;
          selectedGroup: { id: string; name: string } | null | undefined; // null meaning "groupless", undefined meaning don't filter on group
      }
)) => {
    const [groupSearch, setGroupSearch] = useState("");
    const [debouncedGroupSearch] = useDebouncedValue(groupSearch, 200);

    const { data: groupSearchResults, isLoading: groupSearchIsLoading } = useQuery(groupsQueryOptions.search(debouncedGroupSearch));
    const groupSearchCombobox = useCombobox({
        onDropdownClose: () => groupSearchCombobox.resetSelectedOption(),
    });

    return (
        <Combobox
            store={groupSearchCombobox}
            onOptionSubmit={(id) => {
                if (nullable && id == none_literal) {
                    setSelectedGroup(null);
                } else {
                    setSelectedGroup(groupSearchResults?.find((it) => it.id == id)!);
                }
                groupSearchCombobox.closeDropdown();
            }}
        >
            <Combobox.Target>
                <InputBase
                    label="Group"
                    component="button"
                    type="button"
                    w={fullWidth ? "100%" : 200}
                    pointer
                    disabled={disabled}
                    rightSection={
                        groupSearchIsLoading ? (
                            <Loader size={18} />
                        ) : nullable && !!selectedGroup ? (
                            <CloseButton
                                size="sm"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => {
                                    setSelectedGroup(undefined);
                                    setGroupSearch("");
                                }}
                                aria-label="Clear value a"
                            />
                        ) : (
                            <Combobox.Chevron />
                        )
                    }
                    onClick={() => groupSearchCombobox.toggleDropdown()}
                    rightSectionPointerEvents={!selectedGroup ? "none" : undefined}
                    styles={{
                        input: {
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                        },
                    }}
                    required={required}
                >
                    {selectedGroup === null ? "None" : selectedGroup?.name || <Input.Placeholder>Pick value</Input.Placeholder>}
                </InputBase>
            </Combobox.Target>

            <Combobox.Dropdown>
                <Combobox.Search
                    value={groupSearch}
                    onChange={(event) => setGroupSearch(event.currentTarget.value)}
                    placeholder="Search groups"
                    aria-label="Search groups"
                    autoFocus
                />
                <Combobox.Options>
                    {groupSearchIsLoading ? (
                        <Combobox.Empty>Loading....</Combobox.Empty>
                    ) : (
                        <>
                            {nullable && none_literal.toLowerCase().includes(groupSearch.toLowerCase()) && (
                                <Combobox.Option value={none_literal}>None</Combobox.Option>
                            )}
                            {groupSearchResults?.map((it) => (
                                <Combobox.Option value={it.id} key={it.id}>
                                    {it.name}
                                </Combobox.Option>
                            ))}
                            {(groupSearchResults?.length ?? 0) == 0 && !nullable && <Combobox.Empty>No results</Combobox.Empty>}
                        </>
                    )}
                </Combobox.Options>
            </Combobox.Dropdown>
        </Combobox>
    );
};
